/**
 * Messaging feature for the Fyncall Widget
 * Handles sending and receiving messages
 */

import { getState, setHasConversation, incrementUnreadCount } from '../core/state.js';
import { getConfig } from '../core/config.js';
import { sendMessage as apiSendMessage } from '../core/api.js';
import { createMessageElement, addMessageToContainer, createQuickReplies, getContextualQuickReplies } from '../ui/components/messages.js';
import { scrollToBottom } from '../ui/dom.js';
import { showDesktopNotification } from './notifications.js';

/**
 * Messaging callbacks
 */
let messagingCallbacks = {
    onMessageSent: null,
    onMessageReceived: null,
    onTypingStart: null,
    onTypingEnd: null,
    onError: null,
    onCartAction: null
};

/**
 * Initialize messaging feature
 * @param {Object} callbacks - Event callbacks
 */
export function initMessaging(callbacks) {
    messagingCallbacks = { ...messagingCallbacks, ...callbacks };
}

/**
 * Send a message
 * @param {Object} options - Message options
 * @param {string} options.content - Message content
 * @param {HTMLElement} options.messagesContainer - Messages container element
 * @param {HTMLElement} options.inputElement - Input element
 * @param {HTMLElement} options.sendButton - Send button element
 * @param {HTMLElement} options.typingIndicator - Typing indicator element
 * @param {Object} options.cartContext - Optional cart context
 * @returns {Promise<Object|null>} Bot response or null
 */
export async function sendMessage({
    content,
    messagesContainer,
    inputElement,
    sendButton,
    typingIndicator,
    cartContext = null
}) {
    const state = getState();
    const config = getConfig();

    if (!content || !state.session) return null;

    // Clear input if using the input element
    if (inputElement && inputElement.value === content) {
        inputElement.value = '';
    }

    // Disable send button
    if (sendButton) sendButton.disabled = true;

    // Add visitor message to UI
    const visitorMsgEl = createMessageElement({
        content,
        type: 'visitor',
        config
    });
    addMessageToContainer(messagesContainer, visitorMsgEl);

    // Show typing indicator
    if (typingIndicator) {
        typingIndicator.classList.add('show');
        scrollToBottom(messagesContainer);
    }

    // Mark that we have a conversation
    setHasConversation(true);

    // Callback for message sent
    if (messagingCallbacks.onMessageSent) {
        messagingCallbacks.onMessageSent(content);
    }

    try {
        // Send via API
        const response = await apiSendMessage(
            state.session,
            state.visitorToken,
            {
                content,
                messageType: 'text',
                cartContext
            }
        );

        // Hide typing indicator
        if (typingIndicator) {
            typingIndicator.classList.remove('show');
        }

        // Handle bot response
        if (response.bot_response) {
            const botContent = response.bot_response.content;
            const messageMetadata = response.bot_response.message_metadata;

            // Add bot message to UI
            const botMsgEl = createMessageElement({
                content: botContent,
                type: 'bot',
                config,
                messageMetadata,
                agentName: config.agentName,
                agentAvatarUrl: config.agentAvatarUrl
            });
            addMessageToContainer(messagesContainer, botMsgEl);

            // Add quick replies if available
            const quickReplies = getContextualQuickReplies(botContent, messageMetadata);
            if (quickReplies) {
                const repliesEl = createQuickReplies(quickReplies, (action) => {
                    sendMessage({
                        content: action,
                        messagesContainer,
                        inputElement,
                        sendButton,
                        typingIndicator,
                        cartContext
                    });
                });
                if (repliesEl) {
                    addMessageToContainer(messagesContainer, repliesEl);
                }
            }

            // Handle cart actions from AI
            if (messageMetadata?.cart_action && messagingCallbacks.onCartAction) {
                messagingCallbacks.onCartAction(messageMetadata.cart_action);
            }

            // Show desktop notification if enabled
            showDesktopNotification({
                title: config.agentName || 'New Message',
                body: botContent.length > 100 ? botContent.substring(0, 100) + '...' : botContent,
                icon: config.agentAvatarUrl || config.companyLogo
            });
            // Increment unread badge if widget is closed
            if (!getState().isOpen) {
                incrementUnreadCount();
            }

            // Callback for message received
            if (messagingCallbacks.onMessageReceived) {
                messagingCallbacks.onMessageReceived(response.bot_response);
            }

            return response.bot_response;
        }

        return null;
    } catch (error) {
        console.error('[FyncallMessaging] Send error:', error);

        // Hide typing indicator
        if (typingIndicator) {
            typingIndicator.classList.remove('show');
        }

        // Callback for error
        if (messagingCallbacks.onError) {
            messagingCallbacks.onError(error);
        }

        // Add error message
        const errorMsgEl = createMessageElement({
            content: 'Sorry, something went wrong. Please try again.',
            type: 'bot',
            config
        });
        addMessageToContainer(messagesContainer, errorMsgEl);

        return null;
    } finally {
        // Re-enable send button
        if (sendButton) sendButton.disabled = false;
        if (inputElement) inputElement.focus();
    }
}

/**
 * Handle incoming WebSocket message
 * @param {Object} data - WebSocket message data
 * @param {Object} options - Handler options
 * @param {HTMLElement} options.messagesContainer - Messages container
 * @param {HTMLElement} options.typingIndicator - Typing indicator
 */
export function handleIncomingMessage(data, { messagesContainer, typingIndicator }) {
    const config = getConfig();
    const state = getState();

    if (data.type === 'typing' && data.data) {
        // Handle typing indicator
        if (data.data.isTyping && data.data.sender?.type === 'agent') {
            if (typingIndicator) {
                typingIndicator.classList.add('show');
                scrollToBottom(messagesContainer);
            }
            if (messagingCallbacks.onTypingStart) {
                messagingCallbacks.onTypingStart();
            }
        } else {
            if (typingIndicator) {
                typingIndicator.classList.remove('show');
            }
            if (messagingCallbacks.onTypingEnd) {
                messagingCallbacks.onTypingEnd();
            }
        }
    } else if (data.type === 'message' && data.event === 'new_message') {
        const msg = data.data;
        if (msg && msg.sender_type !== 'visitor') {
            // Hide typing indicator
            if (typingIndicator) {
                typingIndicator.classList.remove('show');
            }

            // Add message to UI
            const msgEl = createMessageElement({
                content: msg.content,
                type: 'bot',
                config,
                messageMetadata: msg.message_metadata,
                agentName: config.agentName,
                agentAvatarUrl: config.agentAvatarUrl
            });
            addMessageToContainer(messagesContainer, msgEl);

            // Increment unread count if widget is closed
            if (!state.isOpen) {
                incrementUnreadCount();
            }

            // Handle cart actions
            if (msg.message_metadata?.cart_action && messagingCallbacks.onCartAction) {
                messagingCallbacks.onCartAction(msg.message_metadata.cart_action);
            }

            // Callback
            if (messagingCallbacks.onMessageReceived) {
                messagingCallbacks.onMessageReceived(msg);
            }
        }
    }
}

/**
 * Load previous messages into container
 * @param {Array} messages - Array of message objects
 * @param {HTMLElement} messagesContainer - Messages container
 */
export function loadMessages(messages, messagesContainer) {
    if (!messages || !messagesContainer) return;

    const config = getConfig();

    messages.forEach(msg => {
        const type = msg.sender_type === 'visitor' ? 'visitor' : 'bot';
        const msgEl = createMessageElement({
            content: msg.content,
            type,
            config,
            messageMetadata: msg.message_metadata,
            agentName: config.agentName,
            agentAvatarUrl: config.agentAvatarUrl
        });
        addMessageToContainer(messagesContainer, msgEl, false);
    });

    // Scroll to bottom after loading
    scrollToBottom(messagesContainer, false);
}

// Shared audio context for notification sounds
let sharedAudioContext = null;

/**
 * Get or create shared audio context
 * @returns {AudioContext|null}
 */
function getAudioContext() {
    if (!sharedAudioContext) {
        try {
            sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.warn('[FyncallWidget] Audio not supported:', e.message);
            return null;
        }
    }
    return sharedAudioContext;
}

/**
 * Resume audio context (call on user interaction)
 */
export function resumeAudioContext() {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
    }
}

/**
 * Play notification sound for new message
 */
export function playNotificationSound() {
    const config = getConfig();
    if (!config.soundNotifications) {
        console.log('[FyncallWidget] Sound notifications disabled in config');
        return;
    }

    const audioContext = getAudioContext();
    if (!audioContext) return;

    try {
        // Resume if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
            audioContext.resume().then(() => {
                playBeep(audioContext);
            }).catch((e) => {
                console.warn('[FyncallWidget] Could not resume audio:', e.message);
            });
        } else {
            playBeep(audioContext);
        }
    } catch (e) {
        console.warn('[FyncallWidget] Sound playback failed:', e.message);
    }
}

/**
 * Play the actual beep sound
 * @param {AudioContext} audioContext
 */
function playBeep(audioContext) {
    try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Pleasant notification sound
        oscillator.frequency.value = 880; // A5 note
        oscillator.type = 'sine';

        // Fade in and out for smoother sound
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.15);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);

        console.log('[FyncallWidget] Sound notification played');
    } catch (e) {
        console.warn('[FyncallWidget] Beep failed:', e.message);
    }
}
